import { makeAutoObservable } from 'mobx'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { uiStore } from 'shared/store/uiStore'
import { isLink, logger } from 'shared/lib'
import {
  ComplianceApi,
  checkComplianceUrls,
  type IResponseSampleMessagesData,
} from 'entities/Compliance'
import { type LocalNumbersStepsStore } from 'pages/settings/pages/compliance/pages/localNumbers/store/localNumbersStepsStore'
import { LocalNumbersRoutes } from 'pages/settings/pages/compliance/pages/localNumbers'
import { ExampleModalContent } from 'pages/settings/pages/compliance/ui/ExampleModalContent/ExampleModalContent'
import { SettingsPath } from 'pages/settings/route/settingsPath'
import { sampleMessageExamples } from 'pages/settings/pages/compliance/pages/localNumbers/pages/Messages/lib/constants'

export const sampleMessageMinTextLength = 20
export const sampleMessageMaxTextLength = 1024

export class MessagesStore {
  constructor(private _localNumbersStore: LocalNumbersStepsStore) {
    makeAutoObservable(this)
  }

  private _includeEmbeddedLinks = false
  private _includePhoneNumbers = false
  private _includeAgeGatedContent = false
  private _sampleMessage1 = ''
  private _sampleMessage2 = ''
  private _originalSampleMessage1 = ''
  private _originalSampleMessage2 = ''
  private _shouldResetMessageErrors = false
  private _shouldResetMessage2Errors = false
  privacyPolicyUrl = ''
  termsUrl = ''
  errorPrivacyPolicyUrl = ''
  errorTermsUrl = ''

  get includeEmbeddedLinks() {
    return this._includeEmbeddedLinks
  }

  get includePhoneNumbers() {
    return this._includePhoneNumbers
  }

  get includeAgeGatedContent() {
    return this._includeAgeGatedContent
  }

  get sampleMessage1() {
    return this._sampleMessage1
  }

  get sampleMessage2() {
    return this._sampleMessage2
  }

  get localNumbersStore() {
    return this._localNumbersStore
  }

  get isStep2Disabled() {
    const isWithinLimits = (message: string) =>
      message.length >= sampleMessageMinTextLength && message.length <= sampleMessageMaxTextLength

    return (
      !isWithinLimits(this._sampleMessage1) ||
      !isWithinLimits(this._sampleMessage2) ||
      !isLink(this.privacyPolicyUrl) ||
      !isLink(this.termsUrl)
    )
  }

  get aiGenerationStore() {
    return this.localNumbersStore.complianceLayerStore.aiGenerationStore
  }

  get shouldResetMessageErrors() {
    return this._shouldResetMessageErrors
  }

  get shouldResetMessage2Errors() {
    return this._shouldResetMessage2Errors
  }

  init = (sampleMessagesData: IResponseSampleMessagesData | null) => {
    if (!sampleMessagesData) return

    this._includeAgeGatedContent = sampleMessagesData.includeAgeGatedContent
    this._includeEmbeddedLinks = sampleMessagesData.includeEmbeddedLinks
    this._includePhoneNumbers = sampleMessagesData.includePhoneNumbers
    this.setSampleMessage1(sampleMessagesData.sampleMessage1)
    this.setSampleMessage2(sampleMessagesData.sampleMessage2)
    this.setPrivacyPolicyUrl(sampleMessagesData.privacyPolicyUrl ?? '')
    this.setTermsUrl(sampleMessagesData.termsUrl ?? '')
  }

  campaignMessagesNextClick = () => {
    uiStore.changeRoute({
      path: `/${SettingsPath.compliance.localNumbers.root}/${LocalNumbersRoutes.optInMethod}`,
    })
  }

  setIncludeEmbeddedLinks = () => {
    this._includeEmbeddedLinks = !this.includeEmbeddedLinks
  }

  setIncludePhoneNumbers = () => {
    this._includePhoneNumbers = !this.includePhoneNumbers
  }

  setIncludeAgeGatedContent = () => {
    this._includeAgeGatedContent = !this.includeAgeGatedContent
  }

  setSampleMessage1 = (sampleMessage1: string) => {
    this._sampleMessage1 = sampleMessage1
  }

  setSampleMessage2 = (sampleMessage2: string) => {
    this._sampleMessage2 = sampleMessage2
  }

  getMessageGenerationText = (messageType: 'message1' | 'message2') => {
    const { isMessageGenerated, isMessage2Generated } = this.aiGenerationStore
    const isGenerated = messageType === 'message1' ? isMessageGenerated : isMessage2Generated

    return isGenerated ? 'Generate again' : 'Generate with Salesmsg AI'
  }

  setPrivacyPolicyUrl = (url: string) => {
    this.privacyPolicyUrl = url
    if (this.errorPrivacyPolicyUrl) this.errorPrivacyPolicyUrl = ''
  }

  setErrorPrivacyPolicyUrl = (message: string) => {
    this.errorPrivacyPolicyUrl = message
  }

  setTermsUrl = (url: string) => {
    this.termsUrl = url
    if (this.errorTermsUrl) this.errorTermsUrl = ''
  }

  setErrorTermsUrl = (message: string) => {
    this.errorTermsUrl = message
  }

  private getSampleMessagesData = () => {
    return {
      sampleMessage1: this._sampleMessage1,
      sampleMessage2: this._sampleMessage2,
      includeAgeGatedContent: this._includeAgeGatedContent,
      includeEmbeddedLinks: this._includeEmbeddedLinks,
      includePhoneNumbers: this._includePhoneNumbers,
      privacyPolicyUrl: this.privacyPolicyUrl,
      termsUrl: this.termsUrl,
    }
  }

  generateAISampleMessage = async (messageType: 'message1' | 'message2') => {
    try {
      if (messageType === 'message1') {
        this._originalSampleMessage1 = this._sampleMessage1
      } else {
        this._originalSampleMessage2 = this._sampleMessage2
      }

      const { businessInformation, complianceServiceCampaign } =
        this.localNumbersStore.complianceLayerStore

      const messageData = {
        brand: {
          companyName: businessInformation?.business_name || '',
          website: businessInformation?.website_url || '',
        },
        useCaseData: complianceServiceCampaign?.data?.useCaseData || null,
        sampleMessagesData: this.getSampleMessagesData(),
      }

      if (messageType === 'message1') {
        this.aiGenerationStore['_messageLoading'] = true
      } else {
        this.aiGenerationStore['_message2Loading'] = true
      }

      const result = await this.aiGenerationStore.generate10DLCAIMessage(messageData)

      if (result) {
        if (messageType === 'message1' && result.sampleMessage1) {
          this.setSampleMessage1(result.sampleMessage1)
          this._shouldResetMessageErrors = true

          setTimeout(() => {
            this._shouldResetMessageErrors = false
          }, 0)
        } else if (messageType === 'message2' && result.sampleMessage2) {
          this.setSampleMessage2(result.sampleMessage2)
          this._shouldResetMessage2Errors = true

          setTimeout(() => {
            this._shouldResetMessage2Errors = false
          }, 0)
        }
      }
    } catch (error) {
      logger.error(error)
    } finally {
      if (messageType === 'message1') {
        this.aiGenerationStore['_messageLoading'] = false
        this.aiGenerationStore['_isMessageGenerated'] = true
      } else {
        this.aiGenerationStore['_message2Loading'] = false
        this.aiGenerationStore['_isMessage2Generated'] = true
      }
    }
  }

  resetField = (messageType: 'message1' | 'message2') => {
    if (messageType === 'message1') {
      this.setSampleMessage1(this._originalSampleMessage1)
      this.aiGenerationStore.resetMessage1GenerationFlag()
    } else {
      this.setSampleMessage2(this._originalSampleMessage2)
      this.aiGenerationStore.resetMessage2GenerationFlag()
    }
  }

  openSampleMessageModal = () => {
    const id = 'sampleMessageModal'
    const { activeUseCaseTitle, activeUseCaseValue } = this.localNumbersStore.useCaseStore
    const getExampleMessage = () => {
      const { fullMessage, includeLinksMessage, includePhoneNumbersMessage, defaultMessage } =
        sampleMessageExamples[activeUseCaseValue as string]

      if (this.includeEmbeddedLinks && this.includePhoneNumbers) return fullMessage
      if (this.includeEmbeddedLinks) return includeLinksMessage
      if (this.includePhoneNumbers) return includePhoneNumbersMessage

      return defaultMessage
    }

    modalStore.addModal({
      id,
      width: 540,
      title: 'Sample message example',
      hiddenContent: true,
      ModalContent: () => (
        <ExampleModalContent
          activeUseCase={activeUseCaseTitle || ''}
          example={getExampleMessage()}
          withMustInclude
          includesLinks={this.includeEmbeddedLinks}
          includesPhoneNumbers={this.includePhoneNumbers}
        />
      ),
      primaryAction: {
        text: 'Done',
        onAction: () => modalStore.closeModal(id),
      },
      showCloseButton: false,
    })
  }

  checkUrls = async () => {
    return await checkComplianceUrls({
      privacyPolicyUrl: {
        value: this.privacyPolicyUrl,
        setValue: this.setPrivacyPolicyUrl,
        setError: (message) => {
          this.errorPrivacyPolicyUrl = message
        },
      },
      termsUrl: {
        value: this.termsUrl,
        setValue: this.setTermsUrl,
        setError: (message) => {
          this.errorTermsUrl = message
        },
      },
    })
  }

  campaignMassagesNextClick = async (isCheckWithAI?: boolean) => {
    if (!this.localNumbersStore.campaignId) return

    const { isAiCheckrWillUse, setIsContinueWithIssues, analyzeAI10DLC } =
      this.localNumbersStore.aiSidebarStore

    const step = 2

    try {
      this.localNumbersStore.stepsStore.setStepLoading(true)
      setIsContinueWithIssues(false)

      const isUrlsValid = await this.checkUrls()

      if (!isUrlsValid) {
        this.localNumbersStore.stepsStore.setStepLoading(false)
        return
      }

      const { data } = await ComplianceApi.updateComplianceServicesCampaign({
        step,
        sampleMessagesData: this.getSampleMessagesData(),
        campaignId: this.localNumbersStore.campaignId,
      })

      this.localNumbersStore.complianceLayerStore.set10DLCServiceCampaign(data)

      if (isAiCheckrWillUse && isCheckWithAI) {
        const isShouldRedirect = await analyzeAI10DLC(step)

        if (!isShouldRedirect) return
      }

      uiStore.changeRoute({
        path: `/${SettingsPath.compliance.localNumbers.root}/${LocalNumbersRoutes.optInMethod}`,
      })
    } catch (e) {
      logger.error(e)
    } finally {
      this._localNumbersStore.stepsStore.setStepLoading(false)
    }
  }
}
